import { useEffect, useRef } from 'react';
import { reduced } from '../lib/motion';
import './Method.css';

const STEPS = [
  {
    n: '01',
    t: 'Map',
    dur: 'Week 0',
    d: 'Before any code: users, data model, integrations, failure modes, and what "done" means in one sentence. You leave with an architecture diagram and a scope you can hold me to.',
    out: ['Architecture diagram', 'Fixed scope + estimate', 'Risk list'],
  },
  {
    n: '02',
    t: 'Build',
    dur: 'Weeks 1 - n',
    d: 'Vertical slices shipped weekly to a real environment. You see working software every week instead of a status report, and you can change direction while it is still cheap.',
    out: ['Weekly deploys', 'Staging access', 'Open backlog'],
  },
  {
    n: '03',
    t: 'Harden',
    dur: 'Pre-launch',
    d: 'Load behaviour, auth boundaries, secret hygiene, backups, error tracking and a cost review. The pass nobody demos, and the one that decides whether launch week is calm.',
    out: ['Security pass', 'Monitoring + alerts', 'Restore drill'],
  },
  {
    n: '04',
    t: 'Operate',
    dur: 'Ongoing',
    d: 'Launch, then watch the numbers. Iteration driven by real usage, with an optional retainer for maintenance, new features and infrastructure care.',
    out: ['Live dashboards', 'Iteration cycles', 'Optional retainer'],
  },
];

export default function Method() {
  const trackRef = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || reduced()) return undefined;
    let ticking = false;

    const update = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = r.height + vh * 0.4;
      const passed = vh * 0.75 - r.top;
      const p = Math.max(0, Math.min(1, passed / total));
      el.style.setProperty('--fill', String(p));
      ticking = false;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section id="method" className="band mt">
      <div className="shell mt-grid">

        <aside className="mt-aside">
          <div className="sec-head" data-rv="up">
            <span className="sec-tag"><b>05</b> — Method</span>
            <h2 className="display-md">
              <span className="mask-line"><span>You always know</span></span>
              <span className="mask-line" style={{ '--ml-d': '110ms' }}><span>where it stands.</span></span>
            </h2>
            <p className="lede">
              The same four phases on a two-week integration and on a nine-month platform.
              Each one has a deliverable you can point at, so progress is never a matter
              of opinion.
            </p>
          </div>

          <div className="mt-note" data-rv="up" style={{ '--rv-d': '160ms' }}>
            <span className="mono">Engagement</span>
            <p>Fixed-scope projects, or a monthly retainer for teams that need architecture and delivery on tap.</p>
          </div>
        </aside>

        <div className="mt-track" ref={trackRef}>
          <span className="mt-rail" aria-hidden="true"><i /></span>

          {STEPS.map((s, i) => (
            <article
              className="mt-step"
              key={s.n}
              data-rv="up"
              style={{ '--rv-d': `${i * 110}ms` }}
            >
              <span className="mt-node" aria-hidden="true" />
              <div className="mt-step-head">
                <span className="mt-step-n">{s.n}</span>
                <h3 className="mt-step-t">{s.t}</h3>
                <span className="mt-step-dur">{s.dur}</span>
              </div>
              <p className="mt-step-d">{s.d}</p>
              <ul className="mt-out">
                {s.out.map((o) => (
                  <li key={o}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="4 12 10 18 20 6" />
                    </svg>
                    {o}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
